import { useRef, useEffect, useState } from 'react';
import WhatsAppIcon from '../../components/WhatsAppIcon';

const BUCKET = process.env.REACT_APP_MEDIA_BASE_URL
  ? process.env.REACT_APP_MEDIA_BASE_URL.replace('https://storage.googleapis.com/', '').replace(/\/$/, '')
  : null;

const IMAGE_EXTS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp']);
const VIDEO_EXTS = new Set(['.mp4', '.webm', '.mov', '.m4v']);

// Replaces underscore with space, sets first letter of each word to uppercase and rest to lowercase
function formatCategoryName(name) {
  if (!name) return '';
  return name
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// ── GCS listing — no auth needed, bucket is public ───────────────────────────
async function listBucket() {
  if (!BUCKET) return [];
  const base = `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(BUCKET)}/o`;
  const items = [];
  let pageToken;
  do {
    let url = `${base}?fields=nextPageToken,items(name)`;
    if (pageToken) url += `&pageToken=${encodeURIComponent(pageToken)}`;
    const res = await fetch(url);
    if (!res.ok) break;
    const data = await res.json();
    if (data.items) items.push(...data.items.map((o) => o.name));
    pageToken = data.nextPageToken;
  } while (pageToken);
  return items;
}

function ext(name) {
  return name.slice(name.lastIndexOf('.')).toLowerCase();
}

function gcsUrl(name) {
  return `https://storage.googleapis.com/${BUCKET}/${name}`;
}

// ── Parse GCS objects into top-level galleries and subfolder categories ───────
function parseBucketTree(names) {
  const allNames = new Set(names);
  const galleriesMap = {};
  const sortedNames = [...names].sort();

  for (const name of sortedNames) {
    const fileExt = ext(name);
    const isImage = IMAGE_EXTS.has(fileExt);
    const isVideo = VIDEO_EXTS.has(fileExt);

    // Skip non-media or empty directory placeholders ending with /
    if ((!isImage && !isVideo) || name.endsWith('/')) {
      continue;
    }

    const parts = name.split('/');
    if (parts.length < 2) continue;

    const topLevelKey = parts[0].toLowerCase().trim();
    const subfolderKey =
      parts.length > 2 && parts[1].trim() !== ''
        ? parts[1].toLowerCase().trim()
        : 'geral';

    if (!galleriesMap[topLevelKey]) {
      galleriesMap[topLevelKey] = {
        topLevel: topLevelKey,
        isVideoGallery: false,
        categories: {},
      };
    }

    if (isVideo) {
      galleriesMap[topLevelKey].isVideoGallery = true;
      const base = name.slice(0, name.lastIndexOf('.'));
      const posterName = [...IMAGE_EXTS].map((e) => base + e).find((p) => allNames.has(p));
      const item = {
        src: gcsUrl(name),
        poster: posterName ? gcsUrl(posterName) : undefined,
        name,
        category: subfolderKey,
        type: 'video',
      };
      if (!galleriesMap[topLevelKey].categories[subfolderKey]) {
        galleriesMap[topLevelKey].categories[subfolderKey] = [];
      }
      galleriesMap[topLevelKey].categories[subfolderKey].push(item);
    } else if (isImage) {
      // If the image is a poster for a video in the same folder, don't duplicate as standalone image
      const isVideoPoster = [...VIDEO_EXTS].some((ve) =>
        allNames.has(name.slice(0, name.lastIndexOf('.')) + ve)
      );
      if (isVideoPoster) {
        continue;
      }
      const item = {
        src: gcsUrl(name),
        name,
        category: subfolderKey,
        type: 'image',
      };
      if (!galleriesMap[topLevelKey].categories[subfolderKey]) {
        galleriesMap[topLevelKey].categories[subfolderKey] = [];
      }
      galleriesMap[topLevelKey].categories[subfolderKey].push(item);
    }
  }

  // Consistent ordering: photos first, videos second, then any custom top-level folders alphabetically
  const galleryKeys = Object.keys(galleriesMap).sort((a, b) => {
    const order = { photos: 1, fotografia: 1, videos: 2, audiovisual: 2 };
    const orderA = order[a] ?? 99;
    const orderB = order[b] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b);
  });

  return galleryKeys.map((key) => galleriesMap[key]);
}

function getGalleryMeta(gallery) {
  const { topLevel, isVideoGallery } = gallery;
  const formattedName = formatCategoryName(topLevel);

  if (topLevel === 'photos' || topLevel === 'fotografia') {
    return {
      id: 'fotografia',
      eyebrow: 'Fotografia por',
      titlePrimary: 'O momento que merece',
      titleEm: 'ser eternizado.',
      description: 'Ambientes, objetos e momentos.\nUma seleção dos nossos registros.',
      ctaText: 'Quero fotos assim',
      waTopic: 'Fotografia',
      actionHint: 'Deslize para explorar · Toque para ampliar',
    };
  }

  if (topLevel === 'videos' || topLevel === 'audiovisual') {
    return {
      id: 'videos',
      eyebrow: 'Audiovisual',
      titlePrimary: 'Histórias que ganham',
      titleEm: 'vida em vídeo.',
      description: 'Explore nossos projetos.\nDê o play para assistir.',
      ctaText: 'Quero criar um vídeo assim',
      waTopic: 'Audiovisual',
      actionHint: 'Deslize para explorar · Clique para reproduzir',
    };
  }

  return {
    id: topLevel.replace(/[^a-z0-9_-]/gi, ''),
    eyebrow: `${formattedName} por`,
    titlePrimary: `${formattedName} em`,
    titleEm: 'cada detalhe.',
    description: `Explore nossa seleção de ${formattedName}.\nConheça nossos trabalhos.`,
    ctaText: isVideoGallery ? 'Quero vídeos assim' : 'Quero saber mais',
    waTopic: formattedName,
    actionHint: isVideoGallery
      ? 'Deslize para explorar · Clique para reproduzir'
      : 'Deslize para explorar · Toque para ampliar',
  };
}

// ── Lazy video component ──────────────────────────────────────────────────────
function LazyVideo({ src, poster, label }) {
  const videoRef = useRef(null);
  const [activeSrc, setActiveSrc] = useState(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActiveSrc(src);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  return (
    <video
      ref={videoRef}
      src={activeSrc ?? undefined}
      poster={poster}
      controls
      preload={poster ? 'none' : 'metadata'}
      playsInline
      aria-label={label}
    />
  );
}

// ── Generic Gallery Section Component for each Top-Level Folder ───────────────
function GallerySection({ gallery }) {
  const galleryRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const { categories, isVideoGallery } = gallery;
  const meta = getGalleryMeta(gallery);
  const categoryKeys = Object.keys(categories);
  const allItems = Object.values(categories).flat();

  if (allItems.length === 0) return null;

  const displayedItems =
    activeCategory === 'all' ? allItems : categories[activeCategory] || [];

  function scroll(dir) {
    const el = galleryRef.current;
    if (!el) return;
    const item = el.querySelector('figure, .video-item');
    if (!item) return;
    const step = item.offsetWidth + 20;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: dir * step, behavior: reduced ? 'auto' : 'smooth' });
  }

  function handleCategoryChange(cat) {
    setActiveCategory(cat);
    if (galleryRef.current) {
      galleryRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }

  const waUrl = `https://wa.me/5511986304696?text=${encodeURIComponent(
    `Olá! Vim pelo site da l'ora e quero saber mais sobre ${meta.waTopic}.`
  )}`;

  return (
    <section className="section wrap portfolio" id={meta.id}>
      <div className="heading">
        <div>
          <div className="eyebrow">
            {meta.eyebrow} <span style={{ textTransform: 'none' }}>l’ora</span>
          </div>
          <h2>
            {meta.titlePrimary}
            <br />
            <em>{meta.titleEm}</em>
          </h2>
        </div>
        <div>
          <p>
            {meta.description.split('\n').map((line, i) => (
              <span key={i}>
                {line}
                {i < meta.description.split('\n').length - 1 && <br />}
              </span>
            ))}
          </p>
          <div className="gallery-controls">
            <button
              type="button"
              onClick={() => scroll(-1)}
              aria-label={`Ver itens anteriores de ${meta.waTopic}`}
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              aria-label={`Ver próximos itens de ${meta.waTopic}`}
            >
              →
            </button>
          </div>
        </div>
      </div>

      {categoryKeys.length > 1 && (
        <div
          className="category-filters"
          role="tablist"
          aria-label={`Categorias de ${meta.waTopic}`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeCategory === 'all'}
            className={`cat-btn ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('all')}
          >
            Todas ({allItems.length})
          </button>
          {categoryKeys.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCategory === cat}
              className={`cat-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => handleCategoryChange(cat)}
            >
              {formatCategoryName(cat)} ({categories[cat].length})
            </button>
          ))}
        </div>
      )}

      {isVideoGallery ? (
        <div
          className="video-gallery"
          ref={galleryRef}
          tabIndex={0}
          role="region"
          aria-label={`Galeria de vídeos ${meta.waTopic} da l’ora; deslize para ver mais`}
        >
          {displayedItems.map((v, i) => (
            <div className="video-item" key={v.src}>
              <LazyVideo
                src={v.src}
                poster={v.poster}
                label={`${meta.waTopic} l’ora ${i + 1}`}
              />
              <div className="video-caption">
                <span>
                  {String(i + 1).padStart(2, '0')} / {String(displayedItems.length).padStart(2, '0')}
                </span>
                <span>
                  L’ORA · {formatCategoryName(v.category === 'geral' ? meta.waTopic : v.category).toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="gallery"
          ref={galleryRef}
          tabIndex={0}
          role="region"
          aria-label={`Galeria de fotografias ${meta.waTopic} da l’ora; deslize para ver mais`}
        >
          {displayedItems.map((p, i) => (
            <figure key={p.src}>
              <a
                href={p.src}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Ampliar foto ${i + 1}`}
              >
                <img
                  src={p.src}
                  loading="lazy"
                  alt={`Fotografia l’ora ${i + 1}`}
                  width="1280"
                  height="1920"
                />
              </a>
              <figcaption>
                <span>
                  {String(i + 1).padStart(2, '0')} / {String(displayedItems.length).padStart(2, '0')}
                </span>
                <span>
                  L’ORA · {formatCategoryName(p.category === 'geral' ? meta.waTopic : p.category).toUpperCase()}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      <div className="gallery-bottom">
        <span>{meta.actionHint}</span>
        <a className="link" href={waUrl} target="_blank" rel="noopener noreferrer">
          <WhatsAppIcon />
          <span>
            {meta.ctaText}
            <span className="arrow" aria-hidden="true">
              →
            </span>
          </span>
        </a>
      </div>
    </section>
  );
}

// ── Default export ────────────────────────────────────────────────────────────
export default function Gallery() {
  const [galleries, setGalleries] = useState([]);

  useEffect(() => {
    listBucket()
      .then((names) => {
        const parsed = parseBucketTree(names);
        setGalleries(parsed);
      })
      .catch(() => setGalleries([]));
  }, []);

  if (galleries.length === 0) return null;

  return (
    <>
      {galleries.map((gallery) => (
        <GallerySection key={gallery.topLevel} gallery={gallery} />
      ))}
    </>
  );
}
